/* eslint-disable max-len */
import { useDataModel } from '@thd-nucleus/data-sources';
import proptypes from 'prop-types';
import {
  getMetadataAttributeData,
  getProductSpecificationData,
  getSearchModelFilterData,
  buildHeader
} from '../utils';
import { ENTRY_TYPES } from '../constants';

const { string, bool, shape, arrayOf } = proptypes;

export const useApplianceFuelType = ({
  itemId,
  drawerStatus,
  product,
  metadata,
  search,
  entryType,
  allowHeader,
  isCaching,
  skipEntryTypeCheck
}) => {
  const hasCategory = (drawerStatus?.isDryer || drawerStatus?.isRange);
  const hasType = [
    drawerStatus?.hasElectric,
    drawerStatus?.hasGas,
    drawerStatus?.hasInduction,
    drawerStatus?.hasDualFuel,
    drawerStatus?.hasHeatpump
  ].some(Boolean);
  const hasDrawerStatus = hasCategory && hasType;
  const hasProductData = !!product;
  const hasMetadataData = !!metadata;
  const hasSearchData = !!search;

  const resolvedEntryType = skipEntryTypeCheck || ENTRY_TYPES.includes(entryType);

  const hasExistingDataForProduct = hasDrawerStatus || hasProductData || hasSearchData;
  const {
    data: productData,
    loading: productLoading,
    error: productError
  } = useDataModel('product', {
    variables: {
      itemId
    },
    skip: !itemId || hasExistingDataForProduct
  });

  const finalProductData = productData?.product ?? product;
  const { isSuperSku, parentId } = finalProductData?.identifiers || {};

  const hasExistingDataForMetadata = hasDrawerStatus || hasMetadataData || hasSearchData;
  const {
    data: metadataData,
    loading: metadataLoading,
    error: metadataError
  } = useDataModel('metadata', {
    variables: {
      parentId
    },
    skip: !parentId || !isSuperSku || productLoading || hasExistingDataForMetadata
  });

  const finalMetadataData = metadataData?.metadata ?? metadata;

  const { productTypes = [], productCategories = [], fuelType = '' } = !(productLoading || hasSearchData) ? getProductSpecificationData(finalProductData) : {};
  const { metadataTypes = [], metadataCategories = [], selectedType = '' } = !(productLoading || metadataLoading || hasSearchData) ? getMetadataAttributeData(finalMetadataData) : {};
  const { filterTypes = [], filterCategories = [] } = hasSearchData ? getSearchModelFilterData(search) : {};

  const isLoading = isCaching || productLoading || metadataLoading;
  const hasError = !!productError || !!metadataError;
  const error = {
    productError,
    metadataError
  };

  if (hasError) {
    console.error(error);
  }

  const {
    types = [fuelType, ...productTypes, ...metadataTypes, ...filterTypes].filter((type) => !!type),
    categories = [...productCategories, ...metadataCategories, ...filterCategories].filter((category) => !!category),
    isDryer = (categories?.includes('dryer') || categories?.includes('dryers')),
    isRange = (categories?.includes('range') || categories?.includes('ranges')),
    hasElectric = types?.includes('electric'),
    hasGas = types?.includes('gas'),
    hasInduction = types?.includes('induction'),
    hasDualFuel = types?.includes('dual fuel'),
    hasHeatpump = types?.includes('heatpump')
  } = drawerStatus || {};

  const {
    shouldShowHeader,
    headerName,
    headerLabel
  } = buildHeader({
    allowHeader,
    isDryer,
    metadataTypes,
    fuelType,
    selectedType
  });

  const dataCheck = (itemId || product || search);
  const loadCheck = (isLoading || !hasError);
  const hasResolvedCategory = !!(isDryer || isRange);
  const hasResolvedType = [hasElectric, hasGas, hasInduction, hasDualFuel, hasHeatpump]
    .some(Boolean);
  const isValidAppliance = !!dataCheck && !!loadCheck && hasResolvedCategory && hasResolvedType;
  const fuelTypeWillRender = hasDrawerStatus || (isValidAppliance && resolvedEntryType);

  return {
    drawerStatus: {
      types,
      categories,
      isDryer,
      isRange,
      hasElectric,
      hasGas,
      hasInduction,
      hasDualFuel,
      hasHeatpump
    },
    shouldShowHeader,
    headerName,
    headerLabel,
    isLoading,
    hasError,
    error,
    fuelTypeWillRender
  };
};

useApplianceFuelType.propTypes = {
  itemId: string.isRequired,
  entryType: string.isRequired,
  allowHeader: bool.isRequired,
  isCaching: bool,
  skipEntryTypeCheck: bool,
  drawerStatus: shape({
    types: arrayOf(string).isRequired,
    categories: arrayOf(string).isRequired,
    isDryer: bool,
    isRange: bool,
    hasElectric: bool,
    hasGas: bool,
    hasInduction: bool,
    hasDualFuel: bool,
    hasHeatpump: bool
  }).isRequired,
  product: shape({
    itemId: string,
    dataSource: string,
    specificationGroup: arrayOf(shape({
      specTitle: string,
      specifications: arrayOf(shape({
        specName: string,
        specValue: string
      }))
    }))
  }),
  metadata: shape({
    attributes: arrayOf(shape({
      attributeValues: arrayOf(shape({
        value: string
      })),
      attributeName: string
    })),
    superDuperSku: shape({
      attributes: arrayOf(shape({
        attributeName: string,
        attributeValues: arrayOf(shape({
          selected: bool,
          value: string
        }))
      }))
    })
  }),
  search: shape({
    dimensions: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    })),
    primaryFilters: arrayOf(shape({
      label: string,
      refinements: arrayOf(shape({
        label: string
      }))
    }))
  })
};

useApplianceFuelType.defaultProps = {
  skipEntryTypeCheck: false,
  isCaching: false,
  product: null,
  metadata: null,
  search: null
};
